import { logger } from '../util/logger';
import { BehaviorSubject, Subject } from 'rxjs';
import { SyncClient } from 'twilio-sync';
import { invariant } from '@streem/toolbox';
import { util } from 'protobufjs/minimal';
const LOG_LEVEL = 'info';
export class TwilioWallSync {
    // TODO wire up sync for keeping the local wall items on the remote wall
    // e.g. if I lose connection and add/remote items on the wall, when connection is established
    // add/remove any local items that are not on/off the remote
    constructor(wallItemClass, opts) {
        this.wallItemClass = wallItemClass;
        this.opts = opts;
        this.connectedSubject = new BehaviorSubject(false);
        this.eventSubject = new Subject();
        this.reconnectFailedSubject = new BehaviorSubject(false);
        this.connectionState = 'unknown';
        this.reconnectInProgress = false;
        this.tokenExpiredSubject = new BehaviorSubject(false);
        this.eventsToSendOnConnect = [];
        this.events = this.eventSubject.asObservable();
        this.connected = this.connectedSubject.asObservable();
        this.tokenExpired = this.tokenExpiredSubject.asObservable();
        this.reconnectFailed = this.reconnectFailedSubject.asObservable();
        this.wallSyncConnectionState = this.connectionState;
        this.handleRemoteSyncEvent = async (type, event) => {
            if (event.isLocal)
                return;
            const wallItem = await this.getWallItemFromSyncEvent(event);
            if (!wallItem) {
                logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} No WallItem found for eventType=${type} wallItemEvent=${event}`);
                return;
            }
            // don't spam logs on heartbeat.
            const level = wallItem.type === 'heartbeat' ? 'debug' : 'info';
            logger[level](`TwilioSyncClient wallMapName=${this.wallMapName} received wallItemType=${wallItem.type} eventType=${type} for wallItemId=${wallItem.id}`);
            this.eventSubject.next({
                type: type,
                itemId: wallItem.id,
                itemType: wallItem.type,
                item: wallItem,
            });
        };
        this.isSyncConnected = () => this.connectedSubject.value;
        this.isSyncClientConnected = () => this.connectionState === 'connected';
        this.getWallItemFromSyncEvent = async (event) => {
            const syncData = event?.item?.data ?? event?.previousItemData;
            if (this.isTwilioWallItem(syncData)) {
                return this.decodeWallItem(syncData);
            }
        };
        this.decodeWallItem = async (syncData) => {
            const encodedWallItem = syncData.wall_item_encrypted ?? syncData.wall_item;
            const encrypted = !!syncData.wall_item_encrypted;
            let buffer = util.newBuffer(util.base64.length(encodedWallItem));
            util.base64.decode(encodedWallItem, buffer, 0);
            if (encrypted) {
                const iv = buffer.slice(0, 12);
                const cipherText = buffer.slice(12, buffer.length);
                const decodedByteArray = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, this.encryptionKey, cipherText);
                buffer = new Uint8Array(decodedByteArray);
            }
            return this.wallItemClass.decode(buffer);
        };
        this.encodeWallItem = async (item) => {
            const base64Buffer = this.wallItemClass.encode(item).finish();
            if (!this.encryptionKey) {
                return {
                    wall_item: util.base64.encode(base64Buffer, 0, base64Buffer.length),
                };
            }
            const iv = crypto.getRandomValues(new Uint8Array(12));
            const encryptedBuffer = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, this.encryptionKey, base64Buffer));
            // Append the IV to the front of the byte array for decryption later
            const wallBuffer = new Uint8Array(iv.length + encryptedBuffer.byteLength);
            wallBuffer.set(iv, 0);
            wallBuffer.set(encryptedBuffer, iv.byteLength);
            return {
                wall_item_encrypted: util.base64.encode(wallBuffer, 0, wallBuffer.length),
            };
        };
        this.handleVisibilityChange = () => {
            this.reconnectIfNotConnected().catch(err => {
                logger.error(`TwilioSyncClient wallMapName=${this.wallMapName} reconnectIfNotConnected failed: ${err instanceof Error ? err.message : String(err)}`, err);
            });
        };
    }
    getDefaultRetryUntil() {
        const currentDate = new Date();
        currentDate.setMinutes(currentDate.getMinutes() + 2);
        return currentDate;
    }
    async connect() {
        logger.info(`TwilioSyncClient connecting connectionState=${this.connectionState}`);
        this.connectedSubject.next(false);
        const credentials = this.opts.initialCreds
            ? this.opts.initialCreds
            : await this.opts.refreshToken();
        this.wallMapName = credentials.mapName;
        logger.info(`TwilioSyncClient connecting for wallMapName=${this.wallMapName}`);
        if (this.twilioSyncClient) {
            logger.info(`TwilioSyncClient already exists; updating credentials token connectionState=${this.connectionState}`);
            await this.twilioSyncClient.updateToken(credentials.token);
        }
        else {
            logger.info(`TwilioSyncClient creating new SyncClient connectionState=${this.connectionState}`);
            this.twilioSyncClient = new SyncClient(credentials.token, {
                logLevel: process.env['NODE_ENV'] === 'production' ? 'info' : LOG_LEVEL,
            });
            this.twilioSyncClient.on('connectionError', async (error) => {
                logger.error(`TwilioSyncClient Connection Error for wallMapName=${credentials.mapName} connectionState=${this.connectionState} message=${error.message} error=${JSON.stringify(error)}`);
                // This only handles token expiration errors. Other errors are logged, but no action is taken.
                if (error.terminal) {
                    // Note: there is a bug in the Twilio ConnectionError shape. It is missing the errorCode property even though the docs have it.
                    logger.error(`TwilioSyncClient had a terminal error for wallMapName=${credentials.mapName} connectionState=${this.connectionState} errorCode=${error.errorCode}`);
                    // https://www.twilio.com/docs/api/errors/20104 - token expired
                    if (error.errorCode === 20104) {
                        logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} token has expired, attempting to refresh; connectionState=${this.connectionState}`);
                        const data = await this.opts.refreshToken();
                        await this.twilioSyncClient.updateToken(data.token);
                        await this.reconnect();
                    }
                    else if (error.errorCode === null) {
                        logger.warn(`TwilioSyncClient has null errorCode, attempting reconnect...`);
                        await this.reconnect();
                    }
                }
            });
            this.twilioSyncClient.on('connectionStateChanged', async (newState) => {
                await this.handleConnectionStateChangedEvent(newState, credentials.mapName);
            });
            this.twilioSyncClient
                // https://www.twilio.com/docs/sync/identity-and-access-tokens#handling-token-expiry
                .on('tokenAboutToExpire', async () => {
                logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} token about to expire, attempting to refresh;  connectionState=${this.connectionState}. Attempting to refresh token...`);
                await this.refreshToken();
            });
            this.twilioSyncClient.on('tokenExpired', async () => {
                logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} token expired for wallMapName=${this.wallMapName} connectionState=${this.connectionState}. Attempting to refresh token...`);
                await this.refreshToken();
            });
        }
        try {
            invariant(this.twilioSyncClient, `TwilioSyncClient wallMapName=${this.wallMapName} connect was not called for wallMapName=${this.wallMapName} connectionState=${this.connectionState}`);
            this.syncMap = await this.twilioSyncClient.map(credentials.mapName);
            logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} initialized. wallMapName=${this.wallMapName} connectionState=${this.connectionState}`);
            await this.loadExistingWallItemEvents();
            this.syncMap.on('itemAdded', evt => this.handleRemoteSyncEvent('ADDED', evt));
            this.syncMap.on('itemUpdated', evt => this.handleRemoteSyncEvent('UPDATED', evt));
            this.syncMap.on('itemRemoved', evt => this.handleRemoteSyncEvent('DELETED', evt));
            this.syncMap.on('_subscriptionStateChanged', evt => {
                logger.info(`TwilioSyncClient wallMapName=${this.wallMapName}  received subscriptionStateChanged event=${evt}`);
            });
            logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} initialization completed. wallMapName=${this.wallMapName} connectionState=${this.connectionState}`);
            this.connectedSubject.next(true);
        }
        catch (error) {
            logger.error(`TwilioSyncClient wallMapName=${this.wallMapName} unable to connect. connectionState=${this.connectionState}`, error);
        }
        document.addEventListener('visibilitychange', this.handleVisibilityChange, false);
    }
    async refreshToken() {
        invariant(this.twilioSyncClient, 'TwilioSyncClient.connect was not called');
        try {
            const data = await this.opts.refreshToken();
            await this.twilioSyncClient.updateToken(data.token);
        }
        catch (err) {
            logger.error(`TwilioSyncClient wallMapName=${this.wallMapName} failed to refresh token.  connectionState=${this.connectionState}`, err);
        }
    }
    async handleConnectionStateChangedEvent(newState, mapName) {
        /**
         * Client connection state.
         * Directly reflects connection state of the underlying websocket transport.
         * Possible values are as follows:
         * * 'connecting' - client is offline and connection attempt is in process.
         * * 'connected' - client is online and ready.
         * * 'disconnecting' - client is going offline as disconnection is in process.
         * * 'disconnected' - client is offline and no connection attempt is in process.
         * * 'denied' - client connection is denied because of invalid JWT access token. User must refresh token in order to proceed.
         * * 'error' - client connection is in a permanent erroneous state. Client re-initialization is required.
         */
        const previousState = this.connectionState;
        this.connectionState = newState;
        this.wallSyncConnectionState = newState;
        logger.info(`TwilioSyncClient wallMapName=${mapName} Connection state changed from previousConnectionState=${previousState} to connectionState=${this.connectionState}`);
        if (newState === 'connected') {
            await this.flushEventsOnConnect();
        }
    }
    async reconnect() {
        if (this.reconnectInProgress) {
            logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} reconnect already in progress... connectionState=${this.connectionState}`);
            return;
        }
        this.reconnectInProgress = true;
        logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} attempting to reconnect. connectionState=${this.connectionState}`);
        if (this.twilioSyncClient) {
            logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} reconnect(): TwilioSyncClient shutting down. wallMapName=${this.opts.wallId} connectionState=${this.connectionState}`);
            await this.twilioSyncClient.shutdown();
            this.twilioSyncClient = undefined;
            this.syncMap?.close();
        }
        const MAX_RETRIES = 3;
        const RETRY_DELAY = 2000; // Delay between retries in milliseconds to avoid concurrent reconnection tries
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
            logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} reconnect attempt ${attempt}`);
            try {
                this.opts.initialCreds = null;
                await this.connect();
                if (this.twilioSyncClient) {
                    this.reconnectInProgress = false;
                    this.tokenExpiredSubject.next(false);
                    logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} reconnect success on attempt ${attempt}`);
                    return; // Reconnect successful, exit the retry loop
                }
            }
            catch (e) {
                logger.error(`TwilioSyncClient wallMapName=${this.wallMapName} failed reconnect attempt ${attempt}. exception=${e}`);
            }
            if (attempt < MAX_RETRIES) {
                await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
            }
        }
        // Reconnect failed after maximum retries
        this.reconnectInProgress = false;
        this.tokenExpiredSubject.next(true);
        this.reconnectFailedSubject.next(true);
        logger.error(`TwilioSyncClient wallMapName=${this.wallMapName} reconnect failed after ${MAX_RETRIES} attempts`);
    }
    async disconnect() {
        logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} disconnecting. connectionState=${this.connectionState}`);
        if (!this.isSyncConnected()) {
            logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} failed to disconnect. connectionState=${this.connectionState}`);
            return;
        }
        logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} closing syncMap. connectionState=${this.connectionState}`);
        this.syncMap?.close();
        logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} shutting down. connectionState=${this.connectionState}`);
        await this.twilioSyncClient.shutdown();
        this.twilioSyncClient = undefined;
        this.eventSubject.complete();
        this.opts.onDisconnect();
        this.connectedSubject.next(false);
        this.syncMap = undefined;
        document.removeEventListener('visibilitychange', this.handleVisibilityChange, false);
    }
    async add(item, retryUntil = this.getDefaultRetryUntil()) {
        invariant(this.syncMap, 'TwilioWallSync.connect was not called');
        if (this.isSyncClientConnected()) {
            const wallItem = await this.encodeWallItem(item);
            try {
                await this.syncMap.set(item.id, wallItem);
                logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} ADDED WallItem wallItemType=${item.type} wallItemId=${item.id}`);
            }
            catch (e) {
                logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} failed to ADD wallItemType=${item.type} wallItemId=${item.id}, attempting to reconnect SyncClient and retry`, e);
                this.eventsToSendOnConnect.push({
                    type: 'ADDED',
                    item,
                    itemId: item.id,
                    retryUntil,
                });
            }
        }
        else {
            this.eventsToSendOnConnect.push({ type: 'ADDED', item, itemId: item.id, retryUntil });
            logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} not in 'connected' state but will retry upon reconnect. ADD WallItem wallItemType=${item.type} wallItemId=${item.id}`);
        }
    }
    async update(itemId, item, retryUntil = this.getDefaultRetryUntil()) {
        invariant(this.syncMap, 'TwilioWallSync.connect was not called');
        if (this.isSyncClientConnected()) {
            try {
                const wallItem = await this.encodeWallItem(item);
                await this.syncMap.update(itemId, wallItem);
                // don't spam logs on heartbeat.
                const level = item.type === 'heartbeat' ? 'debug' : 'info';
                logger[level](`TwilioSyncClient wallMapName=${this.wallMapName} UPDATED WallItem wallItemType=${item.type} wallItemId=${item.id}`);
            }
            catch (e) {
                logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} failed to UPDATE wallItemType=${item.type} wallItemId=${item.id}, attempting to reconnect SyncClient and retry`, e);
                this.eventsToSendOnConnect.push({
                    type: 'UPDATED',
                    item,
                    itemId: item.id,
                    retryUntil,
                });
            }
        }
        else {
            this.eventsToSendOnConnect.push({ type: 'UPDATED', item, itemId: item.id, retryUntil });
            logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} not in 'connected' state but will retry upon reconnect. UPDATE WallItem wallItemType=${item.type} wallItemId=${item.id}`);
        }
    }
    async delete(itemId, retryUntil = this.getDefaultRetryUntil()) {
        invariant(this.syncMap, `TwilioSyncClient wallMapName=${this.wallMapName} connect was not called`);
        if (this.isSyncClientConnected()) {
            try {
                await this.syncMap.remove(itemId);
                logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} DELETED wallItemId=${itemId}`);
            }
            catch (e) {
                logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} failed to delete item${itemId} from TwilioSyncMap, attempting to reconnect SyncClient and retry`, e);
                this.eventsToSendOnConnect.push({ type: 'REMOVED', itemId: itemId, retryUntil });
            }
        }
        else {
            this.eventsToSendOnConnect.push({ type: 'REMOVED', itemId, retryUntil });
            logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} not in 'connected' state but will retry upon reconnect. DELETE WallItem wallItemId=${itemId}`);
        }
    }
    async clear() {
        invariant(this.syncMap, 'TwilioWallSync.connect was not called');
        if (this.isSyncClientConnected()) {
            try {
                logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} CLEARING wall and resetting connectionState=${this.connectionState}`);
                await this.syncMap.removeMap();
            }
            catch (e) {
                logger.error(`TwilioSyncClient wallMapName=${this.wallMapName} failed to CLEAR wall, attempting to reconnect SyncClient.`, e);
            }
        }
        else {
            logger.error(`TwilioSyncClient wallMapName=${this.wallMapName} not in 'connected' state, thus failed to CLEAR Wall`);
        }
    }
    async loadExistingWallItemEvents() {
        logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} loading existing wall items connectionState=${this.connectionState}`);
        if (!this.syncMap) {
            logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} failed to attempt to load existing wall items. Wall not initialized connectionState=${this.connectionState}`);
            return;
        }
        let currentPaginator = await this.syncMap.getItems();
        // Get Wall Items from Twilio and decode them.
        while (currentPaginator && currentPaginator.items) {
            const decodeProcesses = currentPaginator.items.map(async (item) => {
                if (!this.isTwilioWallItem(item.data)) {
                    logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} encountered an invalid Twilio Wall Item`, item.data);
                    return;
                }
                const wallItem = await this.decodeWallItem(item.data);
                const event = {
                    type: 'ADDED',
                    itemId: item.key,
                    itemType: wallItem.type,
                    item: wallItem,
                };
                this.eventSubject.next(event);
            });
            // should the statuses of the promises be checked?
            await Promise.allSettled(decodeProcesses);
            if (currentPaginator.hasNextPage) {
                currentPaginator = await currentPaginator.nextPage();
            }
            else {
                break;
            }
        }
    }
    isTwilioWallItem(data) {
        if (!data)
            return false;
        return typeof data.wall_item === 'string' || typeof data.wall_item_encrypted === 'string';
    }
    async reconnectIfNotConnected() {
        logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} checking if connection still good. isSyncClientConnected=${this.isSyncClientConnected()}`);
        if (document.visibilityState === 'visible' && !this.isSyncClientConnected()) {
            logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} Returned to Window and TwilioSyncClient connectionState=${this.connectionState}.  Reconnecting...`);
            await this.reconnect();
        }
    }
    /**
     * Flush events that were requested while not connected
     */
    async flushEventsOnConnect() {
        if (this.eventsToSendOnConnect.length == 0) {
            logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} no events to flush`);
            return;
        }
        if (!this.syncMap) {
            logger.error(`TwilioSyncClient wallMapName=${this.wallMapName} syncMap not yet initialized so cannot flush events. count=${this.eventsToSendOnConnect.length}`);
            return;
        }
        logger.info(`TwilioSyncClient wallMapName=${this.wallMapName} flushing count=${this.eventsToSendOnConnect.length} event(s) that were requested while not connected`);
        const currentDateTime = new Date();
        const events = this.eventsToSendOnConnect.splice(0, this.eventsToSendOnConnect.length);
        while (events.length > 0) {
            const event = events.shift();
            if (event.retryUntil < currentDateTime) {
                logger.warn(`TwilioSyncClient wallMapName=${this.wallMapName} Skipping flush wallitem action=${event.type} wallItemId=${event.itemId} retryUntil=${event.retryUntil} since its past retry time. currentDateTime=${currentDateTime}`);
                continue;
            }
            switch (event.type) {
                case 'ADDED':
                    await this.add(event.item, event.retryUntil);
                    break;
                case 'UPDATED':
                    await this.update(event.item.id, event.item, event.retryUntil);
                    break;
                case 'REMOVED':
                    await this.delete(event.itemId, event.retryUntil);
                    break;
            }
        }
    }
}
//# sourceMappingURL=twilio_wall_sync.js.map